import { useTranslation } from '@silospot/i18n/react';
import {
  type CampaignWasteResult,
  adsEffectivePeriod,
  campaignsForDisplay,
} from '@silospot/shared';
import { toast } from '@silospot/ui';
import { useQuery } from '@tanstack/react-query';
import { Copy, Download, ExternalLink, Megaphone, RefreshCw } from 'lucide-react';
import { type ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Btn,
  Card,
  CardHead,
  Kpi,
  KpiGrid,
  PageHeader,
  Verdict,
} from '../../components/cld';
import { GoogleAdsNoLinkCard, GoogleAdsNoWatchedCard } from '../../components/cld/google-ads-empty';
import { Flag } from '../../components/location';
import { api } from '../../lib/api';
import { fmtDate } from '../../lib/datetime';
import { useSiteAdAccounts } from '../../lib/use-site-ad-accounts';
import { filterAuditToWatched, useWatchedSet } from '../../lib/watched-audit';
import { usePeriodRange } from '../../stores/period-store';

const GADS_PLACEMENTS_URL = 'https://ads.google.com/aw/placementexclusions';

/** Waste-score colour (green < 33, amber < 66, red ≥ 66). */
function wasteTone(score: number): 'clean' | 'suspect' | 'fraud' {
  return score >= 66 ? 'fraud' : score >= 33 ? 'suspect' : 'clean';
}

export function GoogleAdsCampaignsPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: tp } = useTranslation('analytics');
  const range = usePeriodRange();
  const adsPeriod = adsEffectivePeriod(range.period);
  const adsFallback = adsPeriod !== range.period;
  const navigate = useNavigate();
  const nf = new Intl.NumberFormat('fr-FR');
  const money = (eur: number) => `${nf.format(Math.round(eur))} €`;

  const { projectId, connected, accounts, loading } = useSiteAdAccounts();
  const [picked, setPicked] = useState<string | null>(null);
  const cid = picked ?? accounts[0]?.id ?? null;
  const { watchedIds, watched } = useWatchedSet(projectId, cid);

  const auditQ = useQuery({
    queryKey: ['gads-audit', cid, range.period],
    queryFn: () => api.googleAdsAudit(cid as string, range.period),
    enabled: !!cid,
  });
  const detailsQ = useQuery({
    queryKey: ['gads-details', cid, range.period],
    queryFn: () => api.googleAdsDetails(cid as string, range.period),
    enabled: !!cid,
  });
  const ipExclQ = useQuery({
    queryKey: ['gads-ipexcl', cid, projectId],
    queryFn: () => api.googleAdsIpExclusions(cid as string, projectId as string),
    enabled: !!cid && !!projectId,
  });

  const header = (
    <PageHeader
      title={t('gadsp.title')}
      sub={t('gadsp.subtitle')}
      actions={
        connected && accounts.length > 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-[12px] text-text-3">{t('gadsp.account')}</span>
            <select
              value={cid ?? ''}
              onChange={(e) => setPicked(e.target.value)}
              className="rounded-[9px] border border-border-strong bg-surface px-2.5 py-1.5 text-[13px] outline-none focus:border-primary"
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name ? `${a.name} (${a.id})` : a.id}
                </option>
              ))}
            </select>
            <Btn
              icon={RefreshCw}
              onClick={() => {
                void auditQ.refetch();
                void detailsQ.refetch();
              }}
            >
              {t('gadsp.refresh')}
            </Btn>
            <Btn primary onClick={() => navigate(`/projects/${projectId}/ads/manage`)}>
              {t('gadsp.manageCampaigns')}
            </Btn>
          </div>
        ) : undefined
      }
    />
  );

  if (loading) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-40 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  if (!connected) {
    return (
      <div>
        {header}
        <Card className="mx-auto max-w-md py-10 text-center">
          <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-accent-bg text-primary">
            <Megaphone className="h-6 w-6" />
          </span>
          <div className="text-[16px] font-bold text-text">{t('gadsp.notConnectedTitle')}</div>
          <p className="mx-auto mt-1 max-w-xs text-[13px] text-text-2">
            {t('gadsp.notConnectedBody')}
          </p>
          {projectId ? (
            <div className="mt-4">
              <Btn primary onClick={() => navigate(`/projects/${projectId}/settings`)}>
                {t('gadsp.connectCta')}
              </Btn>
            </div>
          ) : null}
        </Card>
      </div>
    );
  }

  if (connected && !loading && accounts.length === 0) {
    return (
      <div>
        {header}
        <GoogleAdsNoLinkCard projectId={projectId} />
      </div>
    );
  }

  // Account linked but no campaign monitored yet → neutral empty state.
  if (connected && !loading && accounts.length > 0 && watchedIds && watchedIds.size === 0) {
    return (
      <div>
        {header}
        <GoogleAdsNoWatchedCard projectId={projectId} />
      </div>
    );
  }

  const audit =
    auditQ.data && watchedIds ? filterAuditToWatched(auditQ.data, watchedIds) : auditQ.data;
  // Show EVERY monitored campaign: an idle one (no in-window activity) is listed with
  // zeroed metrics + "no recent activity", never silently dropped by the waste query.
  const displayCampaigns =
    audit && watched ? campaignsForDisplay(audit.campaigns, watched) : (audit?.campaigns ?? []);
  const exclusions = (detailsQ.data?.placements ?? [])
    .filter((p) => p.conversions < 0.5 && p.costEur > 0)
    .sort((a, b) => b.costEur - a.costEur)
    .slice(0, 50);

  const ipExcl = ipExclQ.data;
  const ipCandidates = ipExcl?.candidates ?? [];
  const ipToExclude = ipCandidates.filter((c) => !c.alreadyExcluded);
  const ipOutOfCampaigns = ipExcl?.outOfCampaignsCount ?? 0;
  const ipImportedAt = ipExcl?.importedAt ?? null;
  const ipImportedCount = ipExcl?.importedCount ?? 0;
  const copyIpList = () => {
    void navigator.clipboard.writeText(ipToExclude.map((c) => c.target).join('\n'));
    toast.success(t('gadsp.copied'));
  };

  const exclusionText = exclusions
    .map((p) => p.placement || p.displayName)
    .filter(Boolean)
    .join('\n');
  const copyList = () => {
    void navigator.clipboard.writeText(exclusionText);
    toast.success(t('gadsp.copied'));
  };
  const exportCsv = () => {
    const head = 'placement,type,cost_eur,clicks,conversions';
    const body = exclusions
      .map(
        (p) =>
          `"${(p.placement || p.displayName).replace(/"/g, '""')}",${p.placementType},${Math.round(p.costEur)},${p.clicks},${p.conversions}`,
      )
      .join('\n');
    const blob = new Blob([`${head}\n${body}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `silospot-exclusions-${cid}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totals = audit?.totals;
  const invalidRate =
    totals && totals.clicks + totals.invalidClicks > 0
      ? Math.round((totals.invalidClicks / (totals.clicks + totals.invalidClicks)) * 1000) / 10
      : 0;

  return (
    <div>
      {header}

      <div className="mb-3 text-[12px] text-text-3">
        {t('gadsp.adsWindow')} : {tp(`period.${adsPeriod}`)}
        {adsFallback ? ` — ${t('gadsp.adsLongSoon')}` : ''}
      </div>

      <KpiGrid cols={4}>
        <Kpi label={t('gadsp.kpiPaidClicks')} value={totals ? nf.format(totals.clicks) : '—'} />
        <Kpi
          label={t('gadsp.kpiInvalidClicks')}
          value={totals ? nf.format(totals.invalidClicks) : '—'}
        />
        <Kpi
          label={t('gadsp.kpiInvalidRate')}
          value={totals ? `${nf.format(invalidRate)} %` : '—'}
        />
        <Kpi label={t('gadsp.kpiWasted')} value={totals ? money(totals.wastedEur) : '—'} />
      </KpiGrid>

      <div className="mt-3 flex items-start gap-2 rounded-lg border border-accent-border bg-accent-bg px-4 py-2.5 text-[12.5px] text-accent-strong">
        <span>ℹ</span>
        <span>{t('gadsp.siloHint')}</span>
      </div>

      {/* Audit & recommendations */}
      <Card className="mt-4 p-0">
        <div className="p-4 pb-2">
          <CardHead title={t('gadsp.auditTitle')} />
        </div>
        {auditQ.isLoading || !audit ? (
          <div className="h-48 animate-pulse bg-neutral-bg" />
        ) : displayCampaigns.length === 0 ? (
          <p className="py-10 text-center text-[13px] text-text-3">{t('gadsp.empty')}</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr>
                  {[
                    t('gadsp.colCampaign'),
                    t('gadsp.colCost'),
                    t('gadsp.colClicks'),
                    t('gadsp.colConv'),
                    t('gadsp.colCpa'),
                    t('gadsp.colWaste'),
                    t('gadsp.colWasted'),
                    t('gadsp.colSignals'),
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`whitespace-nowrap border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase text-text-3 ${i >= 1 && i <= 6 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayCampaigns.map((c) => (
                  <CampaignRow key={c.campaignId} c={c} money={money} nf={nf} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* IP exclusions — ads-only, honest about what the Google API can/can't read */}
      <Card className="mt-4">
        <CardHead title={t('gadsp.ipExclTitle')} sub={t('gadsp.ipExclSub')} />

        <div className="mt-1 mb-2 flex items-start gap-2 rounded-lg border border-border bg-bg px-3.5 py-2 text-[12px] leading-snug text-text-3">
          <span>ℹ</span>
          <span>{t('gadsp.ipExclDoctrine')}</span>
        </div>

        {/* Honest comparison basis: account-level exclusions are not API-readable. */}
        <div className="mb-3 flex items-start gap-2 rounded-lg border border-accent-border bg-accent-bg px-3.5 py-2 text-[12px] leading-snug text-accent-strong">
          <span>ℹ</span>
          <span>
            {ipImportedAt
              ? t('gadsp.ipExclBasisImported', {
                  date: fmtDate(ipImportedAt),
                  n: ipImportedCount,
                })
              : t('gadsp.ipExclBasisNone')}
          </span>
        </div>

        {ipExclQ.isLoading ? (
          <div className="h-24 animate-pulse rounded bg-neutral-bg" />
        ) : (
          <>
            <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
              <div className="text-[13px] font-semibold text-text">
                {t('gadsp.ipExclHeroTitle')}{' '}
                <span className="font-normal text-text-3">({ipToExclude.length})</span>
              </div>
              {ipToExclude.length > 0 ? (
                <Btn onClick={copyIpList}>
                  <Copy className="h-3.5 w-3.5" />
                  {t('gadsp.ipExclCopy')}
                </Btn>
              ) : null}
            </div>

            {ipCandidates.length === 0 ? (
              <p className="py-3 text-[12.5px] text-text-3">{t('gadsp.ipExclHeroEmpty')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr>
                      {[
                        t('gadsp.ipExclColIp'),
                        t('gadsp.ipExclColCountry'),
                        t('gadsp.ipExclColAsn'),
                        t('gadsp.ipExclColScore'),
                        t('gadsp.ipExclColClicks'),
                        '',
                      ].map((h, i) => (
                        <th
                          key={h || `n${i}`}
                          className={`whitespace-nowrap border-b border-border px-3 py-2 text-[11px] font-semibold uppercase text-text-3 ${i === 3 || i === 4 ? 'text-right' : 'text-left'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ipCandidates.map((c) => (
                      <tr
                        key={c.target}
                        className={c.alreadyExcluded ? 'opacity-55' : 'hover:bg-[#fafbfc]'}
                      >
                        <td className="border-b border-[#f1f3f6] px-3 py-2">
                          <div className="font-mono text-[12px] text-text">{c.target}</div>
                          <div className="mt-0.5 text-[11px] text-text-3">
                            {c.kind === 'range'
                              ? t('gadsp.ipExclPool', { n: c.distinctIps })
                              : t(`gadsp.ipExclReason_${c.reason}`)}
                          </div>
                        </td>
                        <td className="border-b border-[#f1f3f6] px-3 py-2">
                          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-text-2">
                            <Flag country={c.country} />
                            {c.country || '—'}
                          </span>
                        </td>
                        <td className="border-b border-[#f1f3f6] px-3 py-2 text-[12px] text-text-2">
                          {c.asn
                            ? `AS${c.asn}${c.asnName ? ` · ${c.asnName}` : ''}`
                            : c.asnName || '—'}
                        </td>
                        <td className="border-b border-[#f1f3f6] px-3 py-2 text-right">
                          <Verdict score={c.score} size="sm" />
                        </td>
                        <td className="border-b border-[#f1f3f6] px-3 py-2 text-right font-mono text-text-2">
                          {nf.format(c.adsClicks)}
                        </td>
                        <td className="border-b border-[#f1f3f6] px-3 py-2 text-right">
                          {c.alreadyExcluded ? (
                            <Badge tone="clean">✓ {t('gadsp.ipExclDone')}</Badge>
                          ) : (
                            <Badge tone="fraud">{t('gadsp.ipExclToExclude')}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Non-campaign suspicious traffic is Cloudflare's job, not a Google exclusion. */}
            {ipOutOfCampaigns > 0 ? (
              <details className="mt-4 rounded-lg border border-border bg-bg/40 px-3.5 py-2.5">
                <summary className="cursor-pointer text-[12.5px] font-semibold text-text-2">
                  {t('gadsp.ipExclOutTitle', { n: ipOutOfCampaigns })}
                </summary>
                <p className="mt-1.5 text-[12px] leading-relaxed text-text-3">
                  {t('gadsp.ipExclOutBody')}
                </p>
              </details>
            ) : null}
          </>
        )}
      </Card>

      {/* Recommended exclusion list */}
      <Card className="mt-4">
        <CardHead
          title={t('gadsp.exclTitle')}
          sub={t('gadsp.exclSub')}
          right={
            exclusions.length > 0 ? (
              <div className="flex items-center gap-2">
                <Btn icon={Copy} onClick={copyList}>
                  {t('gadsp.copy')}
                </Btn>
                <Btn icon={Download} onClick={exportCsv}>
                  {t('gadsp.exportCsv')}
                </Btn>
                <a
                  href={GADS_PLACEMENTS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-[9px] border border-border-strong px-3 py-1.5 text-[13px] font-semibold text-text-2 hover:bg-bg"
                >
                  {t('gadsp.openGoogleAds')} <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </div>
            ) : undefined
          }
        />
        {detailsQ.isLoading ? (
          <div className="h-24 animate-pulse rounded bg-neutral-bg" />
        ) : exclusions.length === 0 ? (
          <p className="py-4 text-[13px] text-text-3">{t('gadsp.exclEmpty')}</p>
        ) : (
          <div className="mt-1 overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <tbody>
                {exclusions.map((p) => (
                  <tr key={p.placement || p.displayName} className="hover:bg-[#fafbfc]">
                    <td className="border-b border-[#f1f3f6] px-3 py-2 font-mono text-[12px] text-text">
                      {p.placement || p.displayName}
                    </td>
                    <td className="border-b border-[#f1f3f6] px-3 py-2 text-[11.5px] text-text-3">
                      {p.placementType}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2 text-right font-mono text-[#b45309]">
                      {money(p.costEur)}
                    </td>
                    <td className="whitespace-nowrap border-b border-[#f1f3f6] px-3 py-2 text-right font-mono text-text-3">
                      {nf.format(p.conversions)} conv.
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-[#f6e2c4] bg-[#fdf4e7] px-[11px] py-[7px] text-[11.5px] leading-snug text-suspect-fg">
        {t('gadsp.estimatedNote')}
      </div>
    </div>
  );
}

function CampaignRow({
  c,
  money,
  nf,
}: {
  c: CampaignWasteResult;
  money: (n: number) => string;
  nf: Intl.NumberFormat;
}): ReactElement {
  const { t } = useTranslation('pages');
  const idle = c.noRecentActivity === true;
  const cpa = c.conversions >= 0.5 ? money(c.costEur / c.conversions) : '—';
  return (
    <tr className="hover:bg-[#fafbfc]">
      <td className="border-b border-[#f1f3f6] px-4 py-2.5">
        <div className={`font-semibold ${idle ? 'text-text-2' : 'text-text'}`}>{c.name || '—'}</div>
        <div className="text-[11px] text-text-3">{c.channelType}</div>
      </td>
      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono">
        {idle ? '—' : money(c.costEur)}
      </td>
      <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono">
        {idle ? '—' : nf.format(c.clicks)}
      </td>
      <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono">
        {idle ? '—' : nf.format(c.conversions)}
      </td>
      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono text-text-2">
        {idle ? '—' : cpa}
      </td>
      <td className="border-b border-[#f1f3f6] px-4 py-2.5 text-right">
        {idle ? (
          <span className="text-[12px] text-text-3">—</span>
        ) : (
          <Badge tone={wasteTone(c.wasteScore)}>{c.wasteScore}</Badge>
        )}
      </td>
      <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-2.5 text-right font-mono text-[#b45309]">
        {idle ? '—' : money(c.wastedEur)}
      </td>
      <td className="border-b border-[#f1f3f6] px-4 py-2.5">
        {idle ? (
          <span className="text-[12px] text-text-3">{t('gadsp.noRecentActivity')}</span>
        ) : c.reasons.length === 0 ? (
          <span className="text-[12px] text-clean-fg">{t('gadsp.noSignal')}</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {c.reasons.map((r) => (
              <span
                key={r.code}
                className="rounded-md border border-[#f3c9c9] bg-fraud-bg px-2 py-0.5 text-[11.5px] text-fraud-fg"
                title={r.detail}
              >
                {t(`gadsr.${r.code}`)}
              </span>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
